import React from "react";
import { useLocation } from "react-router-dom";
import { L as Link, LN as NavLink, useLocaleNavigate } from "../lib/i18n";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { Heart, MessageSquare, LayoutDashboard, LogOut, User, Plus, Globe, Bell, ShieldCheck, Building2 } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { lang, setLang, t, supported } = useLang();
  const navigate = useLocaleNavigate();

  const linkCls = ({ isActive }) =>
    `text-sm tracking-wide transition-colors ${isActive ? "text-white" : "text-zinc-400 hover:text-white"}`;

  return (
    <header
      className="sticky top-0 z-40 w-full border-b border-[var(--ch-line)] bg-[var(--ch-bg-0)]/85 backdrop-blur-xl"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
    >
      <div
        className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8"
        style={{
          paddingLeft: "max(1rem, env(safe-area-inset-left, 0px))",
          paddingRight: "max(1rem, env(safe-area-inset-right, 0px))",
        }}
      >
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <svg width="28" height="28" viewBox="0 0 32 32" className="text-red-600" fill="currentColor" aria-hidden="true">
            <path d="M16 2 28 9v14L16 30 4 23V9z" />
            <path d="M16 11.5 22 15v6l-6 3.5-6-3.5v-6z" fill="#000" />
          </svg>
          <span className="font-display text-lg font-bold tracking-tight text-white">
            Car<span className="text-red-600">Hive</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          <NavLink to="/listings" className={linkCls} data-testid="nav-browse">{t.nav.browse}</NavLink>
          <NavLink to="/map" className={linkCls} data-testid="nav-map">{t.nav.map}</NavLink>
          <NavLink to="/pricing" className={linkCls} data-testid="nav-pricing">{t.nav.pricing}</NavLink>
          <NavLink to="/sell" className={linkCls} data-testid="nav-sell">{t.nav.sell}</NavLink>
          {user && (
            <>
              <NavLink to="/favorites" className={linkCls} data-testid="nav-favorites">{t.nav.favorites}</NavLink>
              <NavLink to="/messages" className={linkCls} data-testid="nav-messages">{t.nav.messages}</NavLink>
            </>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {/* Premium 4-way language switcher */}
          <div className="hidden items-center rounded-full border border-[var(--ch-line)] bg-[var(--ch-bg-1)]/60 p-0.5 backdrop-blur md:flex" data-testid="language-switcher">
            {supported.map((code) => (
              <button
                key={code}
                onClick={() => setLang(code)}
                data-testid={`lang-${code}`}
                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider transition-all ${
                  lang === code ? "bg-[var(--ch-red)] text-white shadow" : "text-zinc-400 hover:text-white"
                }`}
              >
                {code}
              </button>
            ))}
          </div>
          {/* Mobile language dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1 text-zinc-400 hover:bg-[var(--ch-bg-2)] hover:text-white md:hidden" data-testid="language-toggle-mobile">
                <Globe className="h-4 w-4" />
                <span className="text-xs font-semibold uppercase">{lang}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="border-[var(--ch-line)] bg-[var(--ch-bg-1)] text-white">
              {supported.map((code) => (
                <DropdownMenuItem key={code} onClick={() => setLang(code)} className="focus:bg-[var(--ch-bg-2)] uppercase tracking-wider">{code}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 text-white hover:bg-zinc-900" data-testid="user-menu">
                  <div className="grid h-7 w-7 place-items-center rounded-full bg-red-600 text-xs font-bold">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <span className="hidden text-sm sm:inline">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 border-zinc-800 bg-zinc-950 text-white">
                <DropdownMenuItem onClick={() => navigate("/dashboard")} className="focus:bg-zinc-900" data-testid="menu-dashboard">
                  <LayoutDashboard className="mr-2 h-4 w-4" /> {t.nav.dashboard}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/favorites")} className="focus:bg-zinc-900">
                  <Heart className="mr-2 h-4 w-4" /> {t.nav.favorites}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/messages")} className="focus:bg-zinc-900">
                  <MessageSquare className="mr-2 h-4 w-4" /> {t.nav.messages}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/saved-searches")} className="focus:bg-zinc-900" data-testid="menu-searches">
                  <Bell className="mr-2 h-4 w-4" /> Meine Alerts
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/sell")} className="focus:bg-zinc-900">
                  <Plus className="mr-2 h-4 w-4" /> {t.nav.sell}
                </DropdownMenuItem>
                {user.is_dealer && (
                  <DropdownMenuItem onClick={() => navigate("/dealer/profile")} className="focus:bg-zinc-900" data-testid="menu-dealer">
                    <Building2 className="mr-2 h-4 w-4" /> Händler-Profil
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem onClick={() => navigate("/account")} className="focus:bg-zinc-900" data-testid="menu-account">
                  <User className="mr-2 h-4 w-4" /> {t.account?.title || "Konto"}
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} className="focus:bg-zinc-900 text-red-400" data-testid="menu-admin">
                    <ShieldCheck className="mr-2 h-4 w-4" /> Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} className="focus:bg-zinc-900" data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" /> {t.nav.logout}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : user === false ? (
            <>
              <Button asChild variant="ghost" size="sm" className="text-zinc-300 hover:bg-zinc-900 hover:text-white">
                <Link to="/login" data-testid="login-button">{t.nav.login}</Link>
              </Button>
              <Button asChild size="sm" className="bg-red-600 text-white hover:bg-red-500">
                <Link to="/register" data-testid="register-button">{t.nav.register}</Link>
              </Button>
            </>
          ) : null}
        </div>
      </div>
    </header>
  );
}
